/**
 * ConciergeReviewPanel
 *
 * Shows the concierge's proposed draft, grouped by section, with a per-section
 * accept toggle and an editor for each section's fields (services, prices,
 * hours, reminder timing, etc). Nothing here has been saved anywhere —
 * confirming just merges the accepted, possibly-edited sections into the
 * wizard's draft state, exactly as if the owner had typed them into the
 * manual steps. Modeled on the review stage of PhotoImportModal (per-item
 * review and edit before anything is applied).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, Plus, Sparkles, Trash2 } from 'lucide-react';
import { localTimeToUtcMs } from '@tilly/shared';
import {
  AppButton,
  AppDateField,
  AppIconButton,
  AppSelect,
  AppTextField,
  AppTimeField,
  AppToggleSwitch,
  Box,
  Typography,
} from '@/components';
import { adminBusinessApi, adminProvidersApi } from '@/api';
import { colors, fonts, radius, spacing } from '@/theme/tokens';
import { generateSlug, getAvailableTimezones, DAYS_OF_WEEK, TIME_OPTIONS } from '../constants';
import type { OnboardingDraft } from '../types';

const DURATION_VALUES = [15, 30, 45, 60, 90, 120, 180];
const REMINDER_HOUR_VALUES: { value: string; key: string }[] = [
  { value: '1', key: 'oneHour' },
  { value: '4', key: 'fourHours' },
  { value: '24', key: 'oneDay' },
  { value: '48', key: 'twoDays' },
];

export interface ConciergeServiceProposal {
  name: string;
  durationMinutes?: number;
  price?: number;
  category?: string;
  description?: string;
}

export interface ConciergeAppointmentProposal {
  clientId?: string;
  clientName: string;
  clientPhone?: string;
  serviceId: string;
  serviceName?: string;
  providerId?: string;
  providerName?: string;
  startTime: string;
  displayDate: string;
  displayTime: string;
  /** IANA timezone displayDate/displayTime are local to — needed to recompute
   * startTime if the owner edits the date/time in the review panel. */
  timezone: string;
  notes?: string;
}

export interface ConciergeRescheduleProposal {
  appointmentId: string;
  clientName: string;
  serviceName?: string;
  previousDisplayDate: string;
  previousDisplayTime: string;
  displayDate: string;
  displayTime: string;
  startTime: string;
  timezone: string;
}

export interface ConciergeCancellationProposal {
  appointmentId: string;
  clientName: string;
  serviceName?: string;
  displayDate: string;
  displayTime: string;
  reason?: string;
}

export interface ConciergeDraftProposal {
  businessName?: string;
  businessSlug?: string;
  businessType?: string;
  selectedTemplateId?: string;
  services?: ConciergeServiceProposal[];
  selectedTimezone?: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  streetAddress?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  businessHours?: Record<string, { open: string; close: string; closed: boolean }>;
  reminderEnabled?: boolean;
  reminderHours?: number;
  appointment?: ConciergeAppointmentProposal;
  reschedule?: ConciergeRescheduleProposal;
  cancellation?: ConciergeCancellationProposal;
}

type SectionKey =
  | 'basics'
  | 'services'
  | 'hours'
  | 'reminders'
  | 'appointment'
  | 'reschedule'
  | 'cancellation';

function formatPriceDollars(cents?: number): string {
  return cents === undefined ? '' : (cents / 100).toFixed(2);
}

function parsePriceCents(dollars: string): number | undefined {
  const value = Number(dollars);
  return dollars.trim() === '' || Number.isNaN(value) ? undefined : Math.round(value * 100);
}

const TIMEZONE_OPTIONS = getAvailableTimezones().map((tz) => ({ value: tz, label: tz }));

export function ConciergeReviewPanel({
  proposal,
  onConfirm,
  onDiscard,
  discardLabel,
  confirming,
  businessId,
}: {
  proposal: ConciergeDraftProposal;
  onConfirm: (accepted: Partial<OnboardingDraft>) => void;
  onDiscard: () => void;
  discardLabel: string;
  confirming?: boolean;
  /** Enables live service/provider dropdowns for the appointment section.
   * Falls back to read-only display when absent (no known caller omits it
   * for an appointment draft today, but the component stays safe either way). */
  businessId?: string;
}) {
  const { t } = useTranslation('admin');
  const tr = (key: string, options?: Record<string, unknown>) =>
    t(key as never, options as never) as unknown as string;

  const dayLabels: Record<(typeof DAYS_OF_WEEK)[number], string> = {
    monday: t('setupWizard.days.monday'),
    tuesday: t('setupWizard.days.tuesday'),
    wednesday: t('setupWizard.days.wednesday'),
    thursday: t('setupWizard.days.thursday'),
    friday: t('setupWizard.days.friday'),
    saturday: t('setupWizard.days.saturday'),
    sunday: t('setupWizard.days.sunday'),
  };

  const durationOptions = DURATION_VALUES.map((v) => ({
    value: String(v),
    label: tr(`setupWizard.servicesStep.durationOptions.${v}`),
  }));

  const reminderHourOptions = REMINDER_HOUR_VALUES.map(({ value, key }) => ({
    value,
    label: tr(`setupWizard.remindersStep.${key}`),
  }));

  // Which sections exist at all — fixed at mount so clearing a field mid-edit
  // (e.g. blanking the business name) doesn't make the section disappear.
  const [presentSections] = useState<SectionKey[]>(() => {
    const keys: SectionKey[] = [];
    if (proposal.businessName) keys.push('basics');
    if (proposal.services && proposal.services.length > 0) keys.push('services');
    if (proposal.businessHours) keys.push('hours');
    if (proposal.reminderEnabled !== undefined || proposal.reminderHours !== undefined) {
      keys.push('reminders');
    }
    if (proposal.appointment) keys.push('appointment');
    if (proposal.reschedule) keys.push('reschedule');
    if (proposal.cancellation) keys.push('cancellation');
    return keys;
  });

  // Editable working copy of the concierge's proposal. Starts as a straight
  // copy of what the LLM proposed; every field below is editable before confirm.
  const [edited, setEdited] = useState<ConciergeDraftProposal>(() => ({
    ...proposal,
    services: proposal.services?.map((svc) => ({ ...svc })),
    businessHours: proposal.businessHours ? { ...proposal.businessHours } : undefined,
    appointment: proposal.appointment ? { ...proposal.appointment } : undefined,
    reschedule: proposal.reschedule ? { ...proposal.reschedule } : undefined,
    cancellation: proposal.cancellation ? { ...proposal.cancellation } : undefined,
  }));

  const [accepted, setAccepted] = useState<Record<SectionKey, boolean>>(
    () =>
      Object.fromEntries(presentSections.map((key) => [key, true])) as Record<SectionKey, boolean>
  );
  // The appointment/reschedule/cancellation sections start expanded — unlike
  // the settings sections, each is usually the one thing being confirmed, and
  // its fields (who, what, when) need to be visible immediately for the owner
  // to catch a wrong resolution, not hidden behind an extra "show details" click.
  const [expanded, setExpanded] = useState<Partial<Record<SectionKey, boolean>>>(() => ({
    ...(proposal.appointment ? { appointment: true } : {}),
    ...(proposal.reschedule ? { reschedule: true } : {}),
    ...(proposal.cancellation ? { cancellation: true } : {}),
  }));

  const servicesQuery = useQuery({
    queryKey: ['services', businessId],
    queryFn: () => adminBusinessApi.getServices(businessId!),
    enabled: Boolean(businessId && edited.appointment),
  });
  const providersQuery = useQuery({
    queryKey: ['providers', businessId],
    queryFn: () => adminProvidersApi.getProviders(businessId!),
    enabled: Boolean(businessId && edited.appointment),
  });
  const serviceOptions = (servicesQuery.data?.services ?? []).map((s) => ({
    value: s.id,
    label: `${s.name} — ${s.durationMinutes}min${s.price !== undefined ? `, $${(s.price / 100).toFixed(2)}` : ''}`,
  }));
  const providerOptions = (providersQuery.data?.providers ?? []).map((p) => ({
    value: p.userId,
    label: p.name,
  }));

  const patchEdited = (patch: Partial<ConciergeDraftProposal>) =>
    setEdited((prev) => ({ ...prev, ...patch }));

  const updateService = (index: number, patch: Partial<ConciergeServiceProposal>) =>
    setEdited((prev) => ({
      ...prev,
      services: prev.services?.map((svc, i) => (i === index ? { ...svc, ...patch } : svc)),
    }));

  const removeService = (index: number) =>
    setEdited((prev) => ({ ...prev, services: prev.services?.filter((_, i) => i !== index) }));

  const addService = () =>
    setEdited((prev) => ({
      ...prev,
      services: [...(prev.services ?? []), { name: '', durationMinutes: 30, price: undefined }],
    }));

  const patchAppointment = (patch: Partial<ConciergeAppointmentProposal>) =>
    setEdited((prev) =>
      prev.appointment ? { ...prev, appointment: { ...prev.appointment, ...patch } } : prev
    );

  // Editing the date or time changes the local wall-clock values the owner sees,
  // but startTime (sent to the real booking endpoint) is a UTC instant — recompute
  // it here with the same localTimeToUtcMs the worker used to produce it, so the
  // browser and worker never disagree about what a given local time means. Native
  // date/time inputs go through empty/partial intermediate values while typing
  // (e.g. clearing before retyping) — only recompute once both are complete,
  // valid values; otherwise just record the in-progress text and keep the last
  // good startTime until the fields are valid again.
  const updateAppointmentDateTime = (patch: { displayDate?: string; displayTime?: string }) =>
    setEdited((prev) => {
      if (!prev.appointment) return prev;
      const next = { ...prev.appointment, ...patch };
      const isCompleteDate = /^\d{4}-\d{2}-\d{2}$/.test(next.displayDate);
      const isCompleteTime = /^\d{2}:\d{2}$/.test(next.displayTime);
      if (!isCompleteDate || !isCompleteTime) {
        return { ...prev, appointment: next };
      }
      const startMs = localTimeToUtcMs(next.displayDate, next.displayTime, next.timezone);
      return { ...prev, appointment: { ...next, startTime: new Date(startMs).toISOString() } };
    });

  const updateAppointmentService = (serviceId: string) => {
    const service = servicesQuery.data?.services.find((s) => s.id === serviceId);
    patchAppointment({ serviceId, serviceName: service?.name });
  };

  const updateAppointmentProvider = (providerId: string) => {
    const provider = providersQuery.data?.providers.find((p) => p.userId === providerId);
    patchAppointment({ providerId, providerName: provider?.name });
  };

  // Same reasoning as updateAppointmentDateTime: recompute the UTC startTime
  // from the edited local date/time using the shared localTimeToUtcMs, only
  // once both fields hold a complete value.
  const updateRescheduleDateTime = (patch: { displayDate?: string; displayTime?: string }) =>
    setEdited((prev) => {
      if (!prev.reschedule) return prev;
      const next = { ...prev.reschedule, ...patch };
      const isCompleteDate = /^\d{4}-\d{2}-\d{2}$/.test(next.displayDate);
      const isCompleteTime = /^\d{2}:\d{2}$/.test(next.displayTime);
      if (!isCompleteDate || !isCompleteTime) {
        return { ...prev, reschedule: next };
      }
      const startMs = localTimeToUtcMs(next.displayDate, next.displayTime, next.timezone);
      return { ...prev, reschedule: { ...next, startTime: new Date(startMs).toISOString() } };
    });

  const patchCancellation = (patch: Partial<ConciergeCancellationProposal>) =>
    setEdited((prev) =>
      prev.cancellation ? { ...prev, cancellation: { ...prev.cancellation, ...patch } } : prev
    );

  const updateDayHours = (
    day: string,
    patch: Partial<{ open: string; close: string; closed: boolean }>
  ) =>
    setEdited((prev) => ({
      ...prev,
      businessHours: {
        ...prev.businessHours,
        [day]: {
          ...(prev.businessHours?.[day] ?? { open: '09:00', close: '17:00', closed: false }),
          ...patch,
        },
      },
    }));

  const sectionMeta: Record<
    SectionKey,
    { title: string; summary: string; toPartial: () => Partial<OnboardingDraft> }
  > = {
    basics: {
      title: t('setupWizard.conciergeReview.sections.basics.title'),
      summary: edited.businessName || t('setupWizard.conciergeReview.sections.basics.untitled'),
      toPartial: () => ({
        businessName: edited.businessName,
        businessSlug: edited.businessSlug || generateSlug(edited.businessName ?? ''),
        businessType: edited.businessType,
        selectedTemplateId: edited.selectedTemplateId,
        selectedTimezone: edited.selectedTimezone,
        contactName: edited.contactName,
        contactPhone: edited.contactPhone,
        contactEmail: edited.contactEmail,
        streetAddress: edited.streetAddress,
        city: edited.city,
        state: edited.state,
        zipCode: edited.zipCode,
      }),
    },
    services: {
      title: t('setupWizard.conciergeReview.sections.services.title'),
      summary: t('setupWizard.conciergeReview.sections.services.summary', {
        count: edited.services?.length ?? 0,
      }) as string,
      toPartial: () => ({
        services: (edited.services ?? [])
          .filter((svc) => svc.name.trim())
          .map((svc) => ({
            name: { en: svc.name },
            durationMinutes: svc.durationMinutes ?? 30,
            price: svc.price,
            category: svc.category ? { en: svc.category } : undefined,
            description: svc.description ? { en: svc.description } : undefined,
          })),
        serviceSource: 'template' as const,
      }),
    },
    hours: {
      title: t('setupWizard.conciergeReview.sections.hours.title'),
      summary: (() => {
        const hours = edited.businessHours ?? {};
        const openDays = DAYS_OF_WEEK.filter((day) => hours[day] && !hours[day].closed);
        return openDays.length > 0
          ? (t('setupWizard.conciergeReview.sections.hours.summaryOpen', {
              count: openDays.length,
            }) as string)
          : t('setupWizard.conciergeReview.sections.hours.summaryNone');
      })(),
      toPartial: () => ({
        businessHours: { ...edited.businessHours } as OnboardingDraft['businessHours'],
      }),
    },
    reminders: {
      title: t('setupWizard.conciergeReview.sections.reminders.title'),
      summary: edited.reminderEnabled
        ? (t('setupWizard.conciergeReview.sections.reminders.summaryOn', {
            hours: edited.reminderHours ?? 24,
          }) as string)
        : t('setupWizard.conciergeReview.sections.reminders.summaryOff'),
      toPartial: () => ({
        reminderEnabled: edited.reminderEnabled ?? true,
        reminderHours: edited.reminderHours ?? 24,
      }),
    },
    appointment: {
      title: t('setupWizard.conciergeReview.sections.appointment.title'),
      summary: edited.appointment
        ? (t('setupWizard.conciergeReview.sections.appointment.summary', {
            client: edited.appointment.clientName,
            service: edited.appointment.serviceName ?? '',
            date: edited.appointment.displayDate,
            time: edited.appointment.displayTime,
          }) as string)
        : '',
      toPartial: () => ({
        appointment: edited.appointment,
      }),
    },
    reschedule: {
      title: t('setupWizard.conciergeReview.sections.reschedule.title'),
      summary: edited.reschedule
        ? (t('setupWizard.conciergeReview.sections.reschedule.summary', {
            client: edited.reschedule.clientName,
            date: edited.reschedule.displayDate,
            time: edited.reschedule.displayTime,
          }) as string)
        : '',
      toPartial: () => ({
        reschedule: edited.reschedule,
      }),
    },
    cancellation: {
      title: t('setupWizard.conciergeReview.sections.cancellation.title'),
      summary: edited.cancellation
        ? (t('setupWizard.conciergeReview.sections.cancellation.summary', {
            client: edited.cancellation.clientName,
            date: edited.cancellation.displayDate,
            time: edited.cancellation.displayTime,
          }) as string)
        : '',
      toPartial: () => ({
        cancellation: edited.cancellation,
      }),
    },
  };

  const handleConfirm = () => {
    const merged = presentSections
      .filter((key) => accepted[key])
      .reduce<
        Partial<OnboardingDraft>
      >((acc, key) => ({ ...acc, ...sectionMeta[key].toPartial() }), {});
    onConfirm({ ...merged, conciergeSource: 'interview' });
  };

  // "Looks right — continue" reads as an onboarding-wizard step; a lone
  // appointment/reschedule/cancellation proposal is a single, immediate
  // action, so each gets its own framing instead.
  const soleSection = presentSections.length === 1 ? presentSections[0] : undefined;
  const confirmLabel =
    soleSection === 'appointment'
      ? t('setupWizard.conciergeReview.bookAppointmentCta')
      : soleSection === 'reschedule'
        ? t('setupWizard.conciergeReview.moveAppointmentCta')
        : soleSection === 'cancellation'
          ? t('setupWizard.conciergeReview.cancelAppointmentCta')
          : t('setupWizard.conciergeReview.confirmCta');

  const fieldRowStyle = { display: 'flex', gap: spacing[2] };

  return (
    <Box
      style={{
        border: `1px solid ${colors.border}`,
        borderRadius: radius.lg,
        padding: spacing[4],
        backgroundColor: colors.surface,
      }}
    >
      <Box
        style={{ display: 'flex', alignItems: 'center', gap: spacing[2], marginBottom: spacing[3] }}
      >
        <Sparkles size={18} color={colors.accent} />
        <Typography
          style={{
            fontFamily: fonts.heading,
            fontSize: '16px',
            fontWeight: 600,
            color: colors.textPrimary,
          }}
        >
          {t('setupWizard.conciergeReview.title')}
        </Typography>
      </Box>
      <Typography style={{ color: colors.textMuted, fontSize: '13px', marginBottom: spacing[4] }}>
        {t('setupWizard.conciergeReview.subtitle')}
      </Typography>

      <Box style={{ display: 'flex', flexDirection: 'column', gap: spacing[3] }}>
        {presentSections.map((key) => {
          const meta = sectionMeta[key];
          const isExpanded = expanded[key] ?? false;
          return (
            <Box
              key={key}
              style={{ borderRadius: radius.md, backgroundColor: colors.surfaceRaised }}
            >
              <Box
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: spacing[3],
                  gap: spacing[2],
                }}
              >
                <Box>
                  <Typography
                    style={{ fontSize: '14px', fontWeight: 500, color: colors.textPrimary }}
                  >
                    {meta.title}
                  </Typography>
                  <Typography style={{ fontSize: '13px', color: colors.textMuted }}>
                    {meta.summary}
                  </Typography>
                </Box>
                <Box style={{ display: 'flex', alignItems: 'center', gap: spacing[1] }}>
                  <AppIconButton
                    size="sm"
                    variant="ghost"
                    tooltip={
                      isExpanded
                        ? t('setupWizard.conciergeReview.hideDetails')
                        : t('setupWizard.conciergeReview.editDetails')
                    }
                    aria-label={
                      isExpanded
                        ? (t('setupWizard.conciergeReview.hideDetailsAria', {
                            title: meta.title,
                          }) as string)
                        : (t('setupWizard.conciergeReview.showDetailsAria', {
                            title: meta.title,
                          }) as string)
                    }
                    onClick={() => setExpanded((prev) => ({ ...prev, [key]: !isExpanded }))}
                  >
                    {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </AppIconButton>
                  <AppToggleSwitch
                    checked={accepted[key] ?? true}
                    onChange={(checked) => setAccepted((prev) => ({ ...prev, [key]: checked }))}
                  />
                </Box>
              </Box>

              {isExpanded && (
                <Box
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: spacing[3],
                    paddingLeft: spacing[3],
                    paddingRight: spacing[3],
                    paddingBottom: spacing[3],
                    paddingTop: spacing[3],
                    borderTop: `1px solid ${colors.border}`,
                  }}
                >
                  {key === 'basics' && (
                    <>
                      <AppTextField
                        label={t('setupWizard.businessInfo.name')}
                        size="small"
                        value={edited.businessName ?? ''}
                        onChange={(e) => patchEdited({ businessName: e.target.value })}
                      />
                      <AppTextField
                        label={t('setupWizard.conciergeReview.bookingLinkSlug')}
                        size="small"
                        value={edited.businessSlug || generateSlug(edited.businessName ?? '')}
                        onChange={(e) => patchEdited({ businessSlug: e.target.value })}
                      />
                      <AppSelect
                        label={tr('setupWizard.timezoneStep.selected')}
                        value={edited.selectedTimezone ?? ''}
                        onChange={(e) =>
                          patchEdited({ selectedTimezone: e.target.value as string })
                        }
                        options={TIMEZONE_OPTIONS}
                      />
                      <Box style={fieldRowStyle}>
                        <AppTextField
                          fullWidth
                          label={t('setupWizard.businessInfo.contactName')}
                          size="small"
                          value={edited.contactName ?? ''}
                          onChange={(e) => patchEdited({ contactName: e.target.value })}
                        />
                        <AppTextField
                          fullWidth
                          label={t('setupWizard.businessInfo.contactPhone')}
                          size="small"
                          value={edited.contactPhone ?? ''}
                          onChange={(e) => patchEdited({ contactPhone: e.target.value })}
                        />
                      </Box>
                      <AppTextField
                        label={t('setupWizard.businessInfo.contactEmail')}
                        size="small"
                        value={edited.contactEmail ?? ''}
                        onChange={(e) => patchEdited({ contactEmail: e.target.value })}
                      />
                      <AppTextField
                        label={t('setupWizard.businessInfo.streetAddress')}
                        size="small"
                        value={edited.streetAddress ?? ''}
                        onChange={(e) => patchEdited({ streetAddress: e.target.value })}
                      />
                      <Box style={fieldRowStyle}>
                        <AppTextField
                          fullWidth
                          label={t('setupWizard.businessInfo.city')}
                          size="small"
                          value={edited.city ?? ''}
                          onChange={(e) => patchEdited({ city: e.target.value })}
                        />
                        <AppTextField
                          fullWidth
                          label={t('setupWizard.businessInfo.state')}
                          size="small"
                          value={edited.state ?? ''}
                          onChange={(e) => patchEdited({ state: e.target.value })}
                        />
                        <AppTextField
                          fullWidth
                          label={t('setupWizard.businessInfo.zipCode')}
                          size="small"
                          value={edited.zipCode ?? ''}
                          onChange={(e) => patchEdited({ zipCode: e.target.value })}
                        />
                      </Box>
                    </>
                  )}

                  {key === 'services' && (
                    <>
                      {(edited.services ?? []).map((svc, index) => (
                        <Box
                          key={index}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: spacing[2],
                            padding: spacing[2],
                            border: `1px solid ${colors.border}`,
                            borderRadius: radius.md,
                            backgroundColor: colors.surface,
                          }}
                        >
                          <Box
                            style={{ display: 'flex', gap: spacing[2], alignItems: 'flex-start' }}
                          >
                            <AppTextField
                              fullWidth
                              label={tr('setupWizard.servicesStep.serviceName')}
                              size="small"
                              value={svc.name}
                              onChange={(e) => updateService(index, { name: e.target.value })}
                            />
                            <AppIconButton
                              size="sm"
                              variant="ghost"
                              tooltip={t('setupWizard.conciergeReview.removeService')}
                              aria-label={
                                t('setupWizard.conciergeReview.removeServiceAria', {
                                  name:
                                    svc.name ||
                                    t('setupWizard.conciergeReview.serviceFallbackName'),
                                }) as string
                              }
                              onClick={() => removeService(index)}
                            >
                              <Trash2 size={16} color={colors.textMuted} />
                            </AppIconButton>
                          </Box>
                          <Box style={fieldRowStyle}>
                            <AppSelect
                              fullWidth
                              label={tr('setupWizard.servicesStep.duration')}
                              value={String(svc.durationMinutes ?? 30)}
                              onChange={(e) =>
                                updateService(index, { durationMinutes: Number(e.target.value) })
                              }
                              options={durationOptions}
                            />
                            <AppTextField
                              fullWidth
                              label={tr('setupWizard.servicesStep.price')}
                              size="small"
                              type="number"
                              inputProps={{ step: '0.01', min: '0' }}
                              value={formatPriceDollars(svc.price)}
                              onChange={(e) =>
                                updateService(index, { price: parsePriceCents(e.target.value) })
                              }
                            />
                          </Box>
                        </Box>
                      ))}
                      <AppButton variant="ghost" size="sm" onClick={addService}>
                        <Plus size={14} style={{ marginRight: spacing[1] }} />
                        {t('setupWizard.conciergeReview.addService')}
                      </AppButton>
                    </>
                  )}

                  {key === 'hours' && (
                    <>
                      {DAYS_OF_WEEK.filter((day) => edited.businessHours?.[day]).map((day) => {
                        const dayHours = edited.businessHours![day];
                        return (
                          <Box
                            key={day}
                            style={{ display: 'flex', alignItems: 'center', gap: spacing[2] }}
                          >
                            <Typography style={{ fontSize: '13px', width: '90px', flexShrink: 0 }}>
                              {dayLabels[day]}
                            </Typography>
                            {dayHours.closed ? (
                              <Typography
                                style={{ fontSize: '13px', color: colors.textMuted, flex: 1 }}
                              >
                                {tr('setupWizard.hoursStep.closed')}
                              </Typography>
                            ) : (
                              <Box style={{ display: 'flex', gap: spacing[2], flex: 1 }}>
                                <AppSelect
                                  fullWidth
                                  label=""
                                  value={dayHours.open}
                                  onChange={(e) =>
                                    updateDayHours(day, { open: e.target.value as string })
                                  }
                                  options={TIME_OPTIONS}
                                />
                                <AppSelect
                                  fullWidth
                                  label=""
                                  value={dayHours.close}
                                  onChange={(e) =>
                                    updateDayHours(day, { close: e.target.value as string })
                                  }
                                  options={TIME_OPTIONS}
                                />
                              </Box>
                            )}
                            <AppToggleSwitch
                              aria-label={
                                t('setupWizard.conciergeReview.dayClosedAria', {
                                  day: dayLabels[day],
                                }) as string
                              }
                              checked={!dayHours.closed}
                              onChange={(open) => updateDayHours(day, { closed: !open })}
                            />
                          </Box>
                        );
                      })}
                    </>
                  )}

                  {key === 'reminders' && (
                    <>
                      <Box
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography style={{ fontSize: '13px', color: colors.textPrimary }}>
                          {t('setupWizard.conciergeReview.sendReminders')}
                        </Typography>
                        <AppToggleSwitch
                          checked={edited.reminderEnabled ?? true}
                          onChange={(checked) => patchEdited({ reminderEnabled: checked })}
                        />
                      </Box>
                      {edited.reminderEnabled !== false && (
                        <AppSelect
                          label={tr('setupWizard.remindersStep.sendBefore')}
                          value={String(edited.reminderHours ?? 24)}
                          onChange={(e) => patchEdited({ reminderHours: Number(e.target.value) })}
                          options={reminderHourOptions}
                        />
                      )}
                    </>
                  )}

                  {key === 'appointment' && edited.appointment && (
                    <>
                      {businessId ? (
                        <AppSelect
                          label={t('setupWizard.conciergeReview.sections.appointment.service')}
                          value={edited.appointment.serviceId}
                          onChange={(e) => updateAppointmentService(e.target.value as string)}
                          options={
                            serviceOptions.length > 0
                              ? serviceOptions
                              : [
                                  {
                                    value: edited.appointment.serviceId,
                                    label:
                                      edited.appointment.serviceName ??
                                      edited.appointment.serviceId,
                                  },
                                ]
                          }
                          disabled={servicesQuery.isLoading}
                        />
                      ) : (
                        <Typography style={{ fontSize: '13px', color: colors.textMuted }}>
                          {edited.appointment.serviceName ?? edited.appointment.serviceId}
                        </Typography>
                      )}

                      {businessId && providerOptions.length > 0 && (
                        <AppSelect
                          label={t('setupWizard.conciergeReview.sections.appointment.provider')}
                          value={edited.appointment.providerId ?? ''}
                          onChange={(e) => updateAppointmentProvider(e.target.value as string)}
                          options={providerOptions}
                          disabled={providersQuery.isLoading}
                        />
                      )}

                      <Box style={fieldRowStyle}>
                        <AppDateField
                          fullWidth
                          label={t('setupWizard.conciergeReview.sections.appointment.date')}
                          size="small"
                          value={edited.appointment.displayDate}
                          onChange={(e) =>
                            updateAppointmentDateTime({ displayDate: e.target.value })
                          }
                        />
                        <AppTimeField
                          fullWidth
                          label={t('setupWizard.conciergeReview.sections.appointment.time')}
                          size="small"
                          value={edited.appointment.displayTime}
                          onChange={(e) =>
                            updateAppointmentDateTime({ displayTime: e.target.value })
                          }
                        />
                      </Box>

                      <Box style={fieldRowStyle}>
                        <AppTextField
                          fullWidth
                          label={t('setupWizard.conciergeReview.sections.appointment.clientName')}
                          size="small"
                          value={edited.appointment.clientName}
                          onChange={(e) => patchAppointment({ clientName: e.target.value })}
                        />
                        <AppTextField
                          fullWidth
                          label={t('setupWizard.conciergeReview.sections.appointment.clientPhone')}
                          size="small"
                          value={edited.appointment.clientPhone ?? ''}
                          onChange={(e) => patchAppointment({ clientPhone: e.target.value })}
                        />
                      </Box>
                      <AppTextField
                        label={t('setupWizard.conciergeReview.sections.appointment.notes')}
                        size="small"
                        multiline
                        value={edited.appointment.notes ?? ''}
                        onChange={(e) => patchAppointment({ notes: e.target.value })}
                      />
                    </>
                  )}

                  {key === 'reschedule' && edited.reschedule && (
                    <>
                      <Typography style={{ fontSize: '13px', color: colors.textMuted }}>
                        {t('setupWizard.conciergeReview.sections.reschedule.movingFrom', {
                          client: edited.reschedule.clientName,
                          service: edited.reschedule.serviceName ?? '',
                          date: edited.reschedule.previousDisplayDate,
                          time: edited.reschedule.previousDisplayTime,
                        })}
                      </Typography>
                      <Box style={fieldRowStyle}>
                        <AppDateField
                          fullWidth
                          label={t('setupWizard.conciergeReview.sections.appointment.date')}
                          size="small"
                          value={edited.reschedule.displayDate}
                          onChange={(e) =>
                            updateRescheduleDateTime({ displayDate: e.target.value })
                          }
                        />
                        <AppTimeField
                          fullWidth
                          label={t('setupWizard.conciergeReview.sections.appointment.time')}
                          size="small"
                          value={edited.reschedule.displayTime}
                          onChange={(e) =>
                            updateRescheduleDateTime({ displayTime: e.target.value })
                          }
                        />
                      </Box>
                    </>
                  )}

                  {key === 'cancellation' && edited.cancellation && (
                    <>
                      <Typography style={{ fontSize: '13px', color: colors.danger }}>
                        {t('setupWizard.conciergeReview.sections.cancellation.warning', {
                          client: edited.cancellation.clientName,
                          service: edited.cancellation.serviceName ?? '',
                          date: edited.cancellation.displayDate,
                          time: edited.cancellation.displayTime,
                        })}
                      </Typography>
                      <AppTextField
                        label={t('setupWizard.conciergeReview.sections.cancellation.reason')}
                        size="small"
                        multiline
                        value={edited.cancellation.reason ?? ''}
                        onChange={(e) => patchCancellation({ reason: e.target.value })}
                      />
                    </>
                  )}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      <Box
        style={{
          marginTop: spacing[5],
          display: 'flex',
          justifyContent: 'flex-end',
          gap: spacing[3],
        }}
      >
        <AppButton variant="ghost" onClick={onDiscard} disabled={confirming}>
          {discardLabel}
        </AppButton>
        <AppButton
          onClick={handleConfirm}
          disabled={presentSections.length === 0}
          loading={confirming}
        >
          {confirmLabel}
        </AppButton>
      </Box>
    </Box>
  );
}
